import { ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { UserListItem } from '@/types/user';
import { getAccessRoleLabel, getUserAccountType, getUserAccountTypeLabel, getUserStatusLabel } from '@/utils/rbac';
import { cn } from '@/lib/utils';
import { UserAssociatedBrands } from './components/UserAssociatedBrands';
import { UserRowActions } from './components/UserRowActions';
import { formatUserWhatsapp } from './utils/formatUserWhatsapp';

type SortKey = 'name' | 'whatsapp' | 'type' | 'role' | 'status' | 'created_at';

type UserTableProps = {
  users: UserListItem[];
  isLoading: boolean;
  error: string | null;
  sortBy?: string;
  order?: 'asc' | 'desc';
  onSort: (_column: SortKey) => void;
  onEdit: (_user: UserListItem) => void;
  onBlock: (_user: UserListItem) => void;
  onUnblock: (_user: UserListItem) => void;
  onInviteGenerated?: (_inviteUrl: string) => void;
};

const SortableHead = ({
  label,
  column,
  sortBy,
  order,
  onSort,
  className,
}: {
  label: string;
  column: SortKey;
  sortBy?: string;
  order?: 'asc' | 'desc';
  onSort: (_column: SortKey) => void;
  className?: string;
}) => {
  const active = sortBy === column;

  return (
    <TableHead className={cn('whitespace-nowrap', className)}>
      <button
        type="button"
        onClick={() => onSort(column)}
        className="inline-flex items-center gap-1 text-left font-medium text-gray-700"
      >
        {label}
        <span className="inline-flex flex-col -space-y-1">
          <ChevronUp className={cn('h-3 w-3', active && order === 'asc' ? 'text-gray-900' : 'text-gray-400')} />
          <ChevronDown className={cn('h-3 w-3', active && order === 'desc' ? 'text-gray-900' : 'text-gray-400')} />
        </span>
      </button>
    </TableHead>
  );
};

const getStatusClasses = (status: string) => {
  const token = String(status || '').toUpperCase();
  if (token === 'ACTIVE') return 'bg-green-50 text-green-700';
  if (token === 'PENDING') return 'bg-amber-100 text-amber-800';
  return 'bg-red-50 text-red-700';
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

export const UserTable = ({
  users,
  isLoading,
  error,
  sortBy,
  order,
  onSort,
  onEdit,
  onBlock,
  onUnblock,
  onInviteGenerated,
}: UserTableProps) => {
  const safeUsers = Array.isArray(users) ? users : [];

  return (
    <div className="w-full min-w-0 max-w-full overflow-x-auto rounded-lg border border-gray-200 bg-white [&>div]:max-w-full [&>div]:overflow-x-auto">
      <Table className="w-max min-w-full">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <SortableHead label="Nome e e-mail" column="name" sortBy={sortBy} order={order} onSort={onSort} />
            <SortableHead label="Whatsapp" column="whatsapp" sortBy={sortBy} order={order} onSort={onSort} />
            <SortableHead label="Tipo de usuário" column="type" sortBy={sortBy} order={order} onSort={onSort} />
            <SortableHead label="Perfil" column="role" sortBy={sortBy} order={order} onSort={onSort} />
            <TableHead className="whitespace-nowrap">
              <span className="inline-flex items-center gap-1 font-medium text-gray-700">
                Marcas associadas
                <span className="inline-flex flex-col -space-y-1">
                  <ChevronUp className="h-3 w-3 text-gray-400" />
                  <ChevronDown className="h-3 w-3 text-gray-400" />
                </span>
              </span>
            </TableHead>
            <SortableHead label="Status" column="status" sortBy={sortBy} order={order} onSort={onSort} />
            <SortableHead label="Data de criação" column="created_at" sortBy={sortBy} order={order} onSort={onSort} />
            <TableHead className="whitespace-nowrap text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={8} className="p-8 text-center">
                <div className="flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Carregando usuários...</span>
                </div>
              </TableCell>
            </TableRow>
          ) : error ? (
            <TableRow>
              <TableCell colSpan={8} className="p-8 text-center text-red-600">
                {error}
              </TableCell>
            </TableRow>
          ) : safeUsers.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} className="p-16 text-center text-sm text-gray-500">
                Nenhum usuário encontrado
              </TableCell>
            </TableRow>
          ) : (
            safeUsers.map((user) => {
              const accountType = getUserAccountType(user);
              return (
                <TableRow key={user.id}>
                  <TableCell className="whitespace-nowrap p-4">
                    <div className="flex flex-col">
                      <span className="font-semibold text-gray-900">{user.name || 'Nome não informado'}</span>
                      <span className="text-sm text-gray-500">{user.email}</span>
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap p-4 text-sm text-gray-900">
                    {formatUserWhatsapp(user.whatsapp)}
                  </TableCell>
                  <TableCell className="p-4">
                    <span
                      className={cn(
                        'inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium',
                        accountType === 'COLLABORATOR'
                          ? 'bg-black text-white'
                          : 'border border-gray-300 bg-gray-100 text-gray-900'
                      )}
                    >
                      {getUserAccountTypeLabel(user)}
                    </span>
                  </TableCell>
                  <TableCell className="p-4 text-sm text-gray-900">{getAccessRoleLabel(user)}</TableCell>
                  <TableCell className="p-4">
                    <UserAssociatedBrands user={user} />
                  </TableCell>
                  <TableCell className="p-4">
                    <span
                      className={cn(
                        'inline-flex rounded-md px-2 py-0.5 text-xs font-medium',
                        getStatusClasses(user.status)
                      )}
                    >
                      {getUserStatusLabel(user.status)}
                    </span>
                  </TableCell>
                  <TableCell className="whitespace-nowrap p-4 text-sm text-gray-600">
                    {formatDate(user.created_at)}
                  </TableCell>
                  <TableCell className="p-4 text-center">
                    <UserRowActions
                      user={user}
                      onEdit={onEdit}
                      onBlock={onBlock}
                      onUnblock={onUnblock}
                      onInviteGenerated={onInviteGenerated}
                    />
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
};
